import { useState, useMemo } from "react";
import { Panel, Label } from "@/components/SplitLayout";

const STOP = new Set("a an the and or but of to in on for with at by from is are was were be been being this that these those it its as not no so if then than".split(" "));

export default function KeywordDensity() {
  const [text, setText] = useState("");
  const [ignoreStop, setIgnoreStop] = useState(true);

  const { total, rows } = useMemo(() => {
    const tokens = (text.toLowerCase().match(/\b[a-z0-9']+\b/g) || []).filter(
      (w) => w.length > 1 && (!ignoreStop || !STOP.has(w))
    );
    const total = tokens.length;
    const freq = {};
    tokens.forEach((w) => (freq[w] = (freq[w] || 0) + 1));
    const rows = Object.entries(freq)
      .map(([word, count]) => ({ word, count, density: (count / total) * 100 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 20);
    return { total, rows };
  }, [text, ignoreStop]);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Content to analyze</Label>
          <textarea
            data-testid="density-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your article or content here…"
            className="h-[380px] w-full resize-none border border-border bg-card p-5 text-sm leading-relaxed outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <label className="flex items-center gap-3 text-sm">
          <input
            data-testid="density-ignore-stop"
            type="checkbox"
            checked={ignoreStop}
            onChange={(e) => setIgnoreStop(e.target.checked)}
            className="h-4 w-4 accent-[hsl(var(--primary))]"
          />
          Ignore common stop words (the, and, of…)
        </label>
      </div>

      <div className="lg:sticky lg:top-24 lg:self-start">
        <Panel title={`Top keywords · ${total} words analyzed`} testId="density-output">
          {rows.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Results will appear as you type.
            </p>
          ) : (
            <div className="space-y-px">
              {rows.map((r, i) => (
                <div
                  key={r.word}
                  data-testid={`density-row-${i}`}
                  className="relative overflow-hidden bg-background"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/10"
                    style={{ width: `${Math.min(100, r.density * 4)}%` }}
                  />
                  <div className="relative flex items-center justify-between px-4 py-2.5">
                    <span className="font-mono text-sm">{r.word}</span>
                    <span className="flex items-center gap-4 font-mono text-xs">
                      <span className="text-muted-foreground">×{r.count}</span>
                      <span className="w-14 text-right font-bold text-primary">
                        {r.density.toFixed(1)}%
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
